import type { Rng } from '../rng.js'

/**
 * The shop behind Turtley's Trolley.
 *
 * The difficulty here is not the arithmetic — she can already do that in
 * Monkey's Maths. It is working out *which* arithmetic, from a sentence. That
 * is a different skill and the one that makes maths feel hard at school.
 *
 * So everything else is kept deliberately easy. Short sentences, ordinary
 * words, things a child recognises, and prices that look like prices.
 */

export interface Item {
  readonly key: string
  /** As it appears in a sentence: "a pencil", "two pencils". */
  readonly one: string
  readonly many: string
  /** In cents, because money in fractions of a penny is nobody's idea of fun. */
  readonly cents: number
}

/**
 * The stock, priced the way a shop prices things.
 *
 * Realistic to the nearest ten cents, and no item over five euro, so change
 * from a note is always workable in her head. A pencil at €0.40 is a pencil; a
 * pencil at €7.31 is a maths textbook pretending to be one.
 */
export const STOCK: readonly Item[] = [
  { key: 'pencil', one: 'a pencil', many: 'pencils', cents: 40 },
  { key: 'rubber', one: 'a rubber', many: 'rubbers', cents: 50 },
  { key: 'ruler', one: 'a ruler', many: 'rulers', cents: 60 },
  { key: 'sticker', one: 'a sticker', many: 'stickers', cents: 30 },
  { key: 'notebook', one: 'a notebook', many: 'notebooks', cents: 120 },
  { key: 'pen', one: 'a pen', many: 'pens', cents: 90 },
  { key: 'paintbrush', one: 'a paintbrush', many: 'paintbrushes', cents: 250 },
  { key: 'glue', one: 'a glue stick', many: 'glue sticks', cents: 150 },
  { key: 'apple', one: 'an apple', many: 'apples', cents: 70 },
  { key: 'banana', one: 'a banana', many: 'bananas', cents: 45 },
]

/** Money as she would say it: "€1.20", "45c". */
export function money(cents: number): string {
  if (cents < 100) return `${cents}c`

  const euro = Math.floor(cents / 100)
  const rest = cents % 100

  return rest === 0 ? `€${euro}` : `€${euro}.${String(rest).padStart(2, '0')}`
}

/** "two pencils", "a notebook" — the way a person would write it. */
export function countedItem(item: Item, howMany: number): string {
  if (howMany === 1) return item.one

  const words = ['zero', 'one', 'two', 'three', 'four', 'five']
  return `${words[howMany] ?? String(howMany)} ${item.many}`
}

/** Distinct items from the stock, so a shop never lists the same thing twice. */
export function pickItems(rng: Rng, howMany: number): Item[] {
  const remaining = [...STOCK]
  const chosen: Item[] = []

  for (let taken = 0; taken < howMany && remaining.length > 0; taken += 1) {
    const at = rng.int(remaining.length)
    // Non-null: `at` is an index into a list we just checked is not empty.
    chosen.push(remaining[at]!)
    remaining.splice(at, 1)
  }

  return chosen
}

/** The notes she might be handed. Nothing bigger: change from €50 is a chore. */
export const NOTES = [500, 1000, 2000] as const
